import { database } from '@mxa-argos/core/config/firebase';
import { DeveloperModeContext } from '@mxa-argos/core/contexts/developer-mode.context';
import type { UserIdentity } from '@mxa-argos/core/interfaces/user-identity.interface';
import type { UserMode } from '@mxa-argos/core/interfaces/user-mode.interface';
import { useGetIdentity } from '@refinedev/core';
import { onValue, ref, set } from 'firebase/database';
import { type ReactNode, useCallback, useEffect, useState } from 'react';

function modePath(uid: string): string {
  return `maximilianers/${uid}/settings/mode`;
}

export function DeveloperModeProvider({ children }: { children: ReactNode }) {
  const { data: user } = useGetIdentity<UserIdentity>();
  const uid = user?.id;

  const [mode, setMode] = useState<UserMode>('AGENT');
  const [ready, setReady] = useState(false);

  useEffect(() => {
    if (!uid) {
      setMode('AGENT');
      setReady(false);
      return;
    }

    const modeRef = ref(database, modePath(uid));
    const unsubscribe = onValue(modeRef, (snapshot) => {
      const value = snapshot.val();
      setMode(value === 'DEVELOPER' ? 'DEVELOPER' : 'AGENT');
      setReady(true);
    });

    return () => unsubscribe();
  }, [uid]);

  const toggleDeveloperMode = useCallback(() => {
    if (!uid) {
      return;
    }
    const nextMode: UserMode = mode === 'DEVELOPER' ? 'AGENT' : 'DEVELOPER';
    void set(ref(database, modePath(uid)), nextMode);
  }, [uid, mode]);

  return (
    <DeveloperModeContext.Provider
      value={{ isDeveloperMode: mode === 'DEVELOPER', toggleDeveloperMode, ready }}
    >
      {children}
    </DeveloperModeContext.Provider>
  );
}
